import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';
import { getErrorMessage } from '../utils/errorHandler';
import { AppError } from '../utils/AppError';

// In-memory cache for profiles to reduce redundant fetches
const profileCache = new Map<string, { profile: Profile | null; timestamp: number }>();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isInitialized: boolean;
  error: AppError | null;
  clearError: () => void;
  signUp: (email: string, password: string, fullName: string, companyName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  const clearError = () => setError(null);

  const fetchProfile = async (userId: string, forceRefresh = false): Promise<Profile | null> => {
    const cached = profileCache.get(userId);
    if (cached && !forceRefresh && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      console.log('Returning cached profile for user:', userId);
      setProfile(cached.profile);
      return cached.profile;
    }

    console.log(`Fetching profile for user: ${userId} (force refresh: ${forceRefresh})`);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, role')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw new AppError('Failed to fetch user profile.', 500, error);
      }
      
      let finalProfile = data;

      if (!data) {
        console.log('Profile not found, attempting to create from auth metadata...');
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({ id: userId, full_name: authUser.user_metadata.full_name || 'New User' })
            .select('*, role')
            .single();
          if (insertError) {
            throw new AppError('Failed to create user profile.', 500, insertError);
          }
          finalProfile = newProfile;
        }
      }

      setProfile(finalProfile);
      profileCache.set(userId, { profile: finalProfile, timestamp: Date.now() });
      return finalProfile;
    } catch (err) {
      const appError = err instanceof AppError ? err : new AppError('An unexpected error occurred while fetching the profile.', 500, err);
      console.error(appError.message, appError.originalError);
      setError(appError);
      setProfile(null);
      profileCache.delete(userId);
      return null;
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const getInitialSession = async () => {
      try {
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw new AppError('Failed to get initial session.', 500, sessionError);
        }

        if (initialSession) {
          setSession(initialSession);
          setUser(initialSession.user);
          await fetchProfile(initialSession.user.id);
        }
      } catch (err) {
        const appError = err instanceof AppError ? err : new AppError('An unexpected error occurred during initialization.', 500, err);
        console.error(appError.message, appError.originalError);
        setError(appError);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        if (newSession?.user) {
          await fetchProfile(newSession.user.id, true);
        } else {
          setProfile(null);
          profileCache.clear();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const authAction = async <T,>(action: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await action();
    } catch (err: any) {
      const appError = new AppError(getErrorMessage(err), err.status || 500, err);
      console.error(appError.message, appError.originalError);
      setError(appError);
      throw appError;
    } finally {
      setLoading(false);
    }
  };

  const signUp = (email: string, password: string, fullName: string, companyName?: string) =>
    authAction(async () => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            company_name: companyName,
          },
        },
      });
      if (error) throw error;
    });

  const signIn = (email: string, password: string) =>
    authAction(async () => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    });

  const signInWithGoogle = () =>
    authAction(async () => {
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' });
      if (error) throw error;
    });

  const signInWithGithub = () =>
    authAction(async () => {
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'github' });
      if (error) throw error;
    });

  const signOut = () =>
    authAction(async () => {
      const { error } = await supabase.auth.signOut();
      
      // Manually clear all Supabase-related localStorage items
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.startsWith('supabase'))) {
            keysToRemove.push(key);
          }
        }
        if (keysToRemove.length > 0) {
          console.log('Clearing all Supabase auth data from localStorage on sign out:', keysToRemove);
          keysToRemove.forEach(key => localStorage.removeItem(key));
        }
      } catch (error) {
        console.error('Error clearing localStorage on sign out:', error);
      }

      if (error) throw error;
    });

  const updateProfile = (updates: Partial<Profile>) =>
    authAction(async () => {
      if (!user) throw new Error('You must be logged in to update your profile.');
      const { error } = await supabase.from('profiles').update(updates).eq('id', user.id);
      if (error) throw error;
      await fetchProfile(user.id, true);
    });

  const value = {
    session,
    user,
    profile,
    loading,
    isInitialized,
    error,
    clearError,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGithub,
    signOut,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
